import { format, parse } from 'date-fns';
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  DollarSign,
  Mail,
  MessageSquare,
  MessagesSquare,
  Minus,
  Phone,
  UserCheck,
  Users,
  Wrench,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge } from '../components/Badge';
import { HealthScorePanel } from '../components/HealthScorePanel';
import { StatCard } from '../components/StatCard';
import { useChatSseHealth, usePlatformHealth, useProviderHealth } from '../hooks/useAdminData';
import { navigate } from '../lib/navigation';

const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(225, 20%, 16%)',
  border: '1px solid hsl(225, 15%, 25%)',
  borderRadius: '8px',
  color: 'hsl(0, 0%, 95%)',
};

const GRID_STROKE = 'hsl(225, 15%, 18%)';
const AXIS_STROKE = 'hsl(0, 0%, 45%)';
const PRIMARY_COLOR = 'hsl(184, 92%, 56%)';
const SUCCESS_COLOR = 'hsl(142, 71%, 45%)';
const DANGER_COLOR = 'hsl(0, 84%, 60%)';
const WARNING_COLOR = 'hsl(38, 92%, 50%)';
const INFO_COLOR = 'hsl(217, 91%, 60%)';
const PURPLE_COLOR = 'hsl(280, 67%, 60%)';

const CHANNEL_COLORS: Record<string, string> = {
  whatsapp: SUCCESS_COLOR,
  email: INFO_COLOR,
  sms: WARNING_COLOR,
  phone_call: PURPLE_COLOR,
};

const DISPOSITION_COLORS: Record<string, string> = {
  answered: SUCCESS_COLOR,
  no_answer: WARNING_COLOR,
  busy: 'hsl(38, 60%, 40%)',
  voicemail: INFO_COLOR,
  failed: DANGER_COLOR,
  rejected: 'hsl(0, 60%, 40%)',
  initiated: PRIMARY_COLOR,
  ringing: 'hsl(184, 60%, 40%)',
  cancelled: AXIS_STROKE,
};

const DAYS_OPTIONS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 14 days', value: 14 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
];

function ChartSkeleton() {
  return (
    <div className="h-56 flex items-center justify-center">
      <div className="text-text-muted">Loading...</div>
    </div>
  );
}

function TrendIndicator({ value }: { value: number }) {
  if (value === 0) return <Minus size={14} className="text-text-muted" />;
  const positive = value > 0;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`flex items-center gap-0.5 text-xs font-medium ${positive ? 'text-success' : 'text-danger'}`}
    >
      <Icon size={14} />
      {Math.abs(value)}%
    </span>
  );
}

function AlertBadge({ count, label, href }: { count: number; label: string; href?: string }) {
  if (count === 0) return null;
  const content = (
    <>
      <AlertTriangle size={14} className="text-warning" />
      <span className="text-sm text-warning">
        <span className="font-semibold">{count}</span> {label}
      </span>
    </>
  );
  if (href) {
    return (
      <button
        type="button"
        onClick={() => navigate(href)}
        className="flex items-center gap-2 px-3 py-2 bg-warning/10 border border-warning/30 rounded-lg hover:bg-warning/20 transition-colors cursor-pointer"
      >
        {content}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-warning/10 border border-warning/30 rounded-lg">
      {content}
    </div>
  );
}

function SectionHeader({ title, icon: Icon }: { title: string; icon: typeof Mail }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <Icon size={18} className="text-primary" />
      <h2 className="text-lg font-semibold text-text">{title}</h2>
    </div>
  );
}

function providerStatusBadge(status: 'ok' | 'degraded' | 'down'): 'success' | 'warning' | 'danger' {
  return status === 'ok' ? 'success' : status === 'degraded' ? 'warning' : 'danger';
}

function providerDotClass(status: 'ok' | 'degraded' | 'down'): string {
  return status === 'ok' ? 'bg-success' : status === 'degraded' ? 'bg-warning' : 'bg-danger';
}

function MiniStat({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-background rounded-lg border border-border p-3">
      <div className="text-xs text-text-muted mb-1">{label}</div>
      <div className="text-lg font-semibold text-text">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {sub && <div className="text-xs text-text-muted mt-0.5">{sub}</div>}
    </div>
  );
}

export function PlatformHealth() {
  const [days, setDays] = useState(30);
  const { data, isLoading } = usePlatformHealth({ days });
  const { data: providerHealth } = useProviderHealth();
  const { data: sseHealth, isLoading: sseHealthLoading } = useChatSseHealth();

  const formatChartDate = (dateStr: string) => {
    try {
      return format(parse(dateStr, 'yyyy-MM-dd', new Date()), 'MMM d');
    } catch {
      return dateStr;
    }
  };

  const alerts = data?.alerts;
  const hasAlerts =
    alerts &&
    (alerts.whatsappWindowsExpiring > 0 ||
      alerts.staleLeads > 0 ||
      alerts.unassignedLeads > 0 ||
      alerts.pendingToolApprovals > 0 ||
      alerts.failedMessages > 0);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Platform Health</h1>
          <p className="text-sm text-text-muted mt-1">Cross-domain operational metrics</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary transition-colors"
        >
          {DAYS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Alerts Banner */}
      {hasAlerts && (
        <div className="flex flex-wrap gap-2">
          <AlertBadge
            count={alerts.pendingToolApprovals}
            label="pending tool approvals"
            href="/pending-tools"
          />
          <AlertBadge count={alerts.failedMessages} label="failed messages" />
          <AlertBadge count={alerts.whatsappWindowsExpiring} label="WhatsApp windows expiring" />
          <AlertBadge count={alerts.staleLeads} label="stale leads (7d+)" />
          <AlertBadge
            count={alerts.unassignedLeads}
            label="unassigned leads"
            href="/unassigned-leads"
          />
        </div>
      )}

      {/* Health Score + Alerts */}
      <HealthScorePanel days={days} />

      {/* Platform Cost Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          label="Total Platform Cost"
          value={data ? `$${parseFloat(data.platformCost.totalCostUsd).toFixed(2)}` : '$0.00'}
          icon={DollarSign}
          loading={isLoading}
        />
        <StatCard
          label="Messaging Cost"
          value={data ? `$${parseFloat(data.platformCost.messagingCostUsd).toFixed(2)}` : '$0.00'}
          icon={MessageSquare}
          loading={isLoading}
        />
        <StatCard
          label="Voice Cost"
          value={data ? `$${parseFloat(data.platformCost.voiceCostUsd).toFixed(2)}` : '$0.00'}
          icon={Phone}
          loading={isLoading}
        />
        <StatCard
          label="AI Cost"
          value={data ? `$${parseFloat(data.platformCost.aiCostUsd).toFixed(2)}` : '$0.00'}
          icon={Zap}
          loading={isLoading}
        />
      </div>

      {/* ===== MESSAGING SECTION ===== */}
      <div className="bg-surface rounded-xl border border-border p-5">
        <SectionHeader title="Messaging Health" icon={Mail} />
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-5">
          <MiniStat label="Total Sent" value={data?.messaging.totalMessages ?? 0} />
          <MiniStat label="Delivery Rate" value={data ? `${data.messaging.deliveryRate}%` : '-'} />
          <MiniStat label="Read Rate" value={data ? `${data.messaging.readRate}%` : '-'} />
          <MiniStat label="Failed" value={data?.messaging.failedCount ?? 0} />
          <MiniStat
            label="Cost"
            value={data ? `$${parseFloat(data.messaging.totalCostUsd).toFixed(2)}` : '-'}
          />
          <div className="bg-background rounded-lg border border-border p-3 flex items-center gap-2">
            <span className="text-xs text-text-muted">vs prev:</span>
            {data ? (
              <TrendIndicator value={data.messaging.changePercent} />
            ) : (
              <span className="text-text-muted">-</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Daily message trend */}
          <div className="lg:col-span-2">
            <h3 className="text-xs font-medium text-text-muted mb-3">Daily Messages</h3>
            {isLoading ? (
              <ChartSkeleton />
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart
                  data={
                    data?.messaging.dailyTrend.map((d) => ({
                      ...d,
                      date: formatChartDate(d.date),
                    })) ?? []
                  }
                >
                  <defs>
                    <linearGradient id="msgDeliveredGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={SUCCESS_COLOR} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={SUCCESS_COLOR} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                  <XAxis dataKey="date" stroke={AXIS_STROKE} fontSize={11} />
                  <YAxis stroke={AXIS_STROKE} fontSize={11} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Area
                    type="monotone"
                    dataKey="delivered"
                    stroke={SUCCESS_COLOR}
                    strokeWidth={2}
                    fill="url(#msgDeliveredGrad)"
                    name="Delivered"
                  />
                  <Area
                    type="monotone"
                    dataKey="failed"
                    stroke={DANGER_COLOR}
                    strokeWidth={1.5}
                    fill="none"
                    name="Failed"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Channel distribution */}
          <div>
            <h3 className="text-xs font-medium text-text-muted mb-3">By Channel</h3>
            {isLoading ? (
              <ChartSkeleton />
            ) : (data?.messaging.byChannel.length ?? 0) > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={data?.messaging.byChannel.map((c) => ({
                      name: c.channel,
                      value: c.count,
                    }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={75}
                    dataKey="value"
                    paddingAngle={2}
                    label={({ name, value }) => `${name} (${value})`}
                  >
                    {data?.messaging.byChannel.map((c) => (
                      <Cell key={c.channel} fill={CHANNEL_COLORS[c.channel] ?? PRIMARY_COLOR} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-56 flex items-center justify-center text-text-muted text-sm">
                No data
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== VOICE & CALLS + PIPELINE ROW ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Voice */}
        <div className="bg-surface rounded-xl border border-border p-5">
          <SectionHeader title="Voice & Calls" icon={Phone} />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
            <MiniStat label="Total Calls" value={data?.voice.totalCalls ?? 0} />
            <MiniStat label="Answer Rate" value={data ? `${data.voice.answerRate}%` : '-'} />
            <MiniStat
              label="Avg Duration"
              value={data ? `${data.voice.avgDurationSeconds}s` : '-'}
            />
            <MiniStat
              label="Cost"
              value={data ? `$${parseFloat(data.voice.totalCostUsd).toFixed(2)}` : '-'}
            />
            <div className="bg-background rounded-lg border border-border p-3 flex items-center gap-2">
              <span className="text-xs text-text-muted">vs prev:</span>
              {data ? (
                <TrendIndicator value={data.voice.changePercent} />
              ) : (
                <span className="text-text-muted">-</span>
              )}
            </div>
          </div>

          <h3 className="text-xs font-medium text-text-muted mb-3">Call Dispositions</h3>
          {isLoading ? (
            <ChartSkeleton />
          ) : (data?.voice.byDisposition.length ?? 0) > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={data?.voice.byDisposition.map((d) => ({
                  name: d.disposition.replace('_', ' '),
                  ...d,
                }))}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis type="number" stroke={AXIS_STROKE} fontSize={11} />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke={AXIS_STROKE}
                  fontSize={10}
                  width={80}
                />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {data?.voice.byDisposition.map((d) => (
                    <Cell
                      key={d.disposition}
                      fill={DISPOSITION_COLORS[d.disposition] ?? PRIMARY_COLOR}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-text-muted text-sm">
              No call data
            </div>
          )}
        </div>

        {/* Pipeline */}
        <div className="bg-surface rounded-xl border border-border p-5">
          <SectionHeader title="Lead Pipeline" icon={Zap} />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
            <MiniStat label="New Leads" value={data?.pipeline.newLeads ?? 0} />
            <MiniStat label="Stale (7d+)" value={data?.pipeline.staleLeads ?? 0} />
            <MiniStat label="Unassigned" value={data?.pipeline.unassignedLeads ?? 0} />
            <div className="bg-background rounded-lg border border-border p-3 flex items-center gap-2">
              <span className="text-xs text-text-muted">vs prev:</span>
              {data ? (
                <TrendIndicator value={data.pipeline.changePercent} />
              ) : (
                <span className="text-text-muted">-</span>
              )}
            </div>
          </div>

          <h3 className="text-xs font-medium text-text-muted mb-3">Daily New Leads</h3>
          {isLoading ? (
            <ChartSkeleton />
          ) : (data?.pipeline.dailyTrend.length ?? 0) > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart
                data={data?.pipeline.dailyTrend.map((d) => ({
                  ...d,
                  date: formatChartDate(d.date),
                }))}
              >
                <defs>
                  <linearGradient id="leadsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={PRIMARY_COLOR} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={PRIMARY_COLOR} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="date" stroke={AXIS_STROKE} fontSize={11} />
                <YAxis stroke={AXIS_STROKE} fontSize={11} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke={PRIMARY_COLOR}
                  strokeWidth={2}
                  fill="url(#leadsGrad)"
                  name="Leads"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-44 flex items-center justify-center text-text-muted text-sm">
              No lead data
            </div>
          )}

          {/* Source breakdown */}
          {data?.pipeline.bySource && data.pipeline.bySource.length > 0 && (
            <div className="mt-4">
              <h3 className="text-xs font-medium text-text-muted mb-2">Top Sources</h3>
              <div className="flex flex-wrap gap-2">
                {data.pipeline.bySource.slice(0, 6).map((s) => (
                  <Badge key={s.source} variant="default">
                    {s.source.replace('_', ' ')} ({s.count})
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== USER ENGAGEMENT ===== */}
      <div className="bg-surface rounded-xl border border-border p-5">
        <SectionHeader title="User Engagement" icon={Users} />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
          <MiniStat label="Total Active Users" value={data?.users.totalActive ?? 0} />
          <MiniStat label="Active (7d)" value={data?.users.activeLastWeek ?? 0} />
          <MiniStat label="Active (30d)" value={data?.users.activeLastMonth ?? 0} />
          <MiniStat label="Active Orgs (7d)" value={data?.users.activeOrgs7d ?? 0} />
          <MiniStat label="Active Orgs (30d)" value={data?.users.activeOrgs30d ?? 0} />
          <div className="bg-background rounded-lg border border-border p-3">
            <div className="text-xs text-text-muted mb-1">Churn Risk</div>
            <div
              className={`text-lg font-semibold ${(data?.users.churnRiskPercent ?? 0) > 30 ? 'text-danger' : (data?.users.churnRiskPercent ?? 0) > 15 ? 'text-warning' : 'text-success'}`}
            >
              {data?.users.churnRiskPercent ?? 0}%
            </div>
            <div className="text-xs text-text-muted">
              {data?.users.inactiveRisk ?? 0} inactive 14d+
            </div>
          </div>
        </div>

        {/* User engagement bar visual */}
        <h3 className="text-xs font-medium text-text-muted mb-3">Engagement Funnel</h3>
        <div className="space-y-2">
          {[
            { label: 'Total Active', value: data?.users.totalActive ?? 0, color: AXIS_STROKE },
            { label: 'Active 30d', value: data?.users.activeLastMonth ?? 0, color: INFO_COLOR },
            { label: 'Active 7d', value: data?.users.activeLastWeek ?? 0, color: SUCCESS_COLOR },
          ].map(({ label, value, color }) => {
            const max = data?.users.totalActive || 1;
            const pct = Math.round((value / max) * 100);
            return (
              <div key={label} className="flex items-center gap-3">
                <span className="text-xs text-text-muted w-20">{label}</span>
                <div className="flex-1 bg-background rounded-full h-5 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, backgroundColor: color }}
                  />
                </div>
                <span className="text-xs text-text-secondary w-12 text-right">{value}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== PROVIDER HEALTH ===== */}
      <div className="bg-surface rounded-xl border border-border p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity size={18} className="text-primary" />
            <h2 className="text-lg font-semibold text-text">Provider Health</h2>
          </div>
          {providerHealth && (
            <Badge variant={providerStatusBadge(providerHealth.status)}>
              {providerHealth.status}
            </Badge>
          )}
        </div>
        {providerHealth ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {providerHealth.checks.map((c) => (
              <div
                key={c.name}
                className="flex items-center gap-2 bg-background rounded-lg border border-border p-3"
              >
                <span
                  className={`inline-block w-2 h-2 rounded-full shrink-0 ${providerDotClass(c.status)}`}
                />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-text capitalize">{c.name}</div>
                  <div className="text-xs text-text-muted truncate" title={c.message}>
                    {c.status}
                    {c.latencyMs != null ? ` · ${c.latencyMs}ms` : ''}
                    {c.message ? ` · ${c.message}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-text-muted">Loading provider status…</div>
        )}
      </div>

      {/* ===== CHAT SSE HEALTH (D10) ===== */}
      <div className="bg-surface rounded-xl border border-border p-5">
        <SectionHeader title="Chat SSE Health" icon={MessagesSquare} />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <MiniStat
            label="Subscriptions"
            value={sseHealthLoading ? '-' : (sseHealth?.subscribeCount ?? 0)}
          />
          <MiniStat
            label="Ready Timeouts"
            value={sseHealthLoading ? '-' : (sseHealth?.readyTimeoutCount ?? 0)}
          />
          <MiniStat
            label="Closed Channels"
            value={sseHealthLoading ? '-' : (sseHealth?.closedChannelCount ?? 0)}
          />
        </div>
        <p className="text-xs text-text-muted mt-3 italic">
          Counters are per-process/in-memory — this reflects a single backend instance, not a
          platform-wide total.
        </p>
      </div>

      {/* ===== AI & MODELS ===== */}
      <div className="bg-surface rounded-xl border border-border p-5">
        <SectionHeader title="AI & Models" icon={Zap} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <MiniStat
            label="AI Cost"
            value={data?.ai ? `$${parseFloat(data.ai.totalCostUsd).toFixed(2)}` : '-'}
          />
          <MiniStat label="Executions" value={data?.ai?.totalExecutions ?? 0} />
          {data?.ai?.byProvider.map((p) => (
            <MiniStat
              key={p.provider}
              label={`${p.provider} cost`}
              value={`$${parseFloat(p.costUsd).toFixed(2)}`}
              sub={`${p.executionCount.toLocaleString()} runs`}
            />
          ))}
        </div>

        <h3 className="text-xs font-medium text-text-muted mb-3">By Model</h3>
        {isLoading ? (
          <ChartSkeleton />
        ) : (data?.ai?.byModel.length ?? 0) > 0 ? (
          <div className="space-y-2">
            {data?.ai?.byModel.map((m) => {
              const cost = parseFloat(m.costUsd);
              const aiTotal = parseFloat(data?.ai?.totalCostUsd ?? '1');
              const pct = aiTotal > 0 ? Math.round((cost / aiTotal) * 100) : 0;
              return (
                <div key={`${m.provider}:${m.model}`} className="flex items-center gap-3">
                  <span className="text-xs text-text-muted w-40 truncate" title={m.model}>
                    {m.model}
                  </span>
                  <Badge variant={m.provider === 'anthropic' ? 'primary' : 'info'}>
                    {m.provider}
                  </Badge>
                  <div className="flex-1 bg-background rounded-full h-4 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.max(pct, 2)}%`,
                        backgroundColor: m.provider === 'anthropic' ? PRIMARY_COLOR : INFO_COLOR,
                      }}
                    />
                  </div>
                  <span className="text-xs text-text-secondary w-16 text-right">
                    ${cost.toFixed(2)}
                  </span>
                  <span className="text-xs text-text-muted w-14 text-right">
                    {m.executionCount.toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="h-24 flex items-center justify-center text-text-muted text-sm">
            No AI usage in this period
          </div>
        )}
      </div>

      {/* ===== EMAIL HEALTH + PROVIDER COSTS ROW ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email Health */}
        <div className="bg-surface rounded-xl border border-border p-5">
          <SectionHeader title="Email Health" icon={Mail} />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
            <MiniStat label="Emails Sent" value={data?.email.totalSent ?? 0} />
            <MiniStat label="Delivery Rate" value={data ? `${data.email.deliveryRate}%` : '-'} />
            <MiniStat label="Failed" value={data?.email.failedCount ?? 0} />
            <MiniStat
              label="Cost"
              value={data ? `$${parseFloat(data.email.totalCostUsd).toFixed(2)}` : '-'}
            />
            <div className="bg-background rounded-lg border border-border p-3 flex items-center gap-2">
              <span className="text-xs text-text-muted">vs prev:</span>
              {data ? (
                <TrendIndicator value={data.email.changePercent} />
              ) : (
                <span className="text-text-muted">-</span>
              )}
            </div>
          </div>

          {(data?.email.byStatus.length ?? 0) > 0 && (
            <div className="mb-5">
              <h3 className="text-xs font-medium text-text-muted mb-3">Delivery Funnel</h3>
              <div className="space-y-2">
                {data?.email.byStatus
                  .slice()
                  .sort((a, b) => b.count - a.count)
                  .map(({ status, count }) => {
                    const pct =
                      data.email.totalSent > 0
                        ? Math.round((count / data.email.totalSent) * 100)
                        : 0;
                    const color = status === 'failed' ? DANGER_COLOR : INFO_COLOR;
                    return (
                      <div key={status} className="flex items-center gap-3">
                        <span className="text-xs text-text-muted w-20 capitalize">{status}</span>
                        <div className="flex-1 bg-background rounded-full h-4 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: color }}
                          />
                        </div>
                        <span className="text-xs text-text-secondary w-16 text-right">
                          {count.toLocaleString()}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          <h3 className="text-xs font-medium text-text-muted mb-3">Daily Emails</h3>
          {isLoading ? (
            <ChartSkeleton />
          ) : (data?.email.dailyTrend.length ?? 0) > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart
                data={data?.email.dailyTrend.map((d) => ({
                  ...d,
                  date: formatChartDate(d.date),
                }))}
              >
                <defs>
                  <linearGradient id="emailGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={INFO_COLOR} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={INFO_COLOR} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="date" stroke={AXIS_STROKE} fontSize={11} />
                <YAxis stroke={AXIS_STROKE} fontSize={11} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Area
                  type="monotone"
                  dataKey="delivered"
                  stroke={INFO_COLOR}
                  strokeWidth={2}
                  fill="url(#emailGrad)"
                  name="Delivered"
                />
                <Area
                  type="monotone"
                  dataKey="failed"
                  stroke={DANGER_COLOR}
                  strokeWidth={1.5}
                  fill="none"
                  name="Failed"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-text-muted text-sm">
              No email data
            </div>
          )}
        </div>

        {/* Provider Costs */}
        <div className="bg-surface rounded-xl border border-border p-5">
          <SectionHeader title="Cost by Provider" icon={DollarSign} />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
            <MiniStat
              label="AI"
              value={data ? `$${parseFloat(data.platformCost.aiCostUsd).toFixed(2)}` : '-'}
            />
            <MiniStat
              label="Messaging"
              value={data ? `$${parseFloat(data.platformCost.messagingCostUsd).toFixed(2)}` : '-'}
            />
            <MiniStat
              label="Voice"
              value={data ? `$${parseFloat(data.platformCost.voiceCostUsd).toFixed(2)}` : '-'}
            />
          </div>

          <h3 className="text-xs font-medium text-text-muted mb-3">Breakdown by Provider</h3>
          {isLoading ? (
            <ChartSkeleton />
          ) : (data?.providerCosts.length ?? 0) > 0 ? (
            <div className="space-y-2">
              {data?.providerCosts.map(({ provider, costUsd, count }) => {
                const cost = parseFloat(costUsd);
                const totalCost = parseFloat(data?.platformCost.totalCostUsd ?? '1');
                const pct = totalCost > 0 ? Math.round((cost / totalCost) * 100) : 0;
                const colorMap: Record<string, string> = {
                  google: INFO_COLOR,
                  anthropic: PRIMARY_COLOR,
                  twilio: PURPLE_COLOR,
                  resend: PRIMARY_COLOR,
                  meta: SUCCESS_COLOR,
                  apify: WARNING_COLOR,
                  elevenlabs: 'hsl(340, 82%, 52%)',
                  serper: 'hsl(200, 98%, 48%)',
                };
                return (
                  <div key={provider} className="flex items-center gap-3">
                    <span className="text-xs text-text-muted w-20 capitalize">{provider}</span>
                    <div className="flex-1 bg-background rounded-full h-4 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.max(pct, 2)}%`,
                          backgroundColor: colorMap[provider] ?? AXIS_STROKE,
                        }}
                      />
                    </div>
                    <span className="text-xs text-text-secondary w-16 text-right">
                      ${cost.toFixed(2)}
                    </span>
                    <span className="text-xs text-text-muted w-12 text-right">{count}x</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center text-text-muted text-sm">
              No provider cost data
            </div>
          )}
        </div>
      </div>

      {/* ===== TOOL ACTIONS + WORKFLOWS ROW ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tool Actions */}
        <div className="bg-surface rounded-xl border border-border p-5">
          <SectionHeader title="Tool Actions" icon={Wrench} />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
            <MiniStat label="Total Actions" value={data?.toolActions.total ?? 0} />
            <MiniStat label="Pending Now" value={data?.toolActions.pendingNow ?? 0} />
            <MiniStat
              label="Rejection Rate"
              value={data ? `${data.toolActions.rejectionRate}%` : '-'}
            />
          </div>

          <h3 className="text-xs font-medium text-text-muted mb-3">By Status</h3>
          {isLoading ? (
            <ChartSkeleton />
          ) : (data?.toolActions.byStatus.length ?? 0) > 0 ? (
            <div className="space-y-2">
              {data?.toolActions.byStatus.map(({ status, count, avgDurationMs }) => {
                const max = data?.toolActions.total || 1;
                const pct = Math.round((count / max) * 100);
                const colorMap: Record<string, string> = {
                  completed: SUCCESS_COLOR,
                  approved: INFO_COLOR,
                  pending: WARNING_COLOR,
                  rejected: DANGER_COLOR,
                  failed: DANGER_COLOR,
                  executing: PRIMARY_COLOR,
                };
                return (
                  <div key={status} className="flex items-center gap-3">
                    <span className="text-xs text-text-muted w-20 capitalize">{status}</span>
                    <div className="flex-1 bg-background rounded-full h-4 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${pct}%`,
                          backgroundColor: colorMap[status] ?? AXIS_STROKE,
                        }}
                      />
                    </div>
                    <span className="text-xs text-text-secondary w-10 text-right">{count}</span>
                    {avgDurationMs > 0 && (
                      <span className="text-xs text-text-muted w-16 text-right">
                        {avgDurationMs}ms
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center text-text-muted text-sm">
              No tool action data
            </div>
          )}
        </div>

        {/* Workflows + Lead Sources */}
        <div className="space-y-6">
          <div className="bg-surface rounded-xl border border-border p-5">
            <SectionHeader title="Workflow Executions" icon={UserCheck} />
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-background rounded-lg border border-border p-3 text-center">
                <div className="text-lg font-semibold text-info">
                  {data?.workflows.running ?? 0}
                </div>
                <div className="text-xs text-text-muted">Running</div>
              </div>
              <div className="bg-background rounded-lg border border-border p-3 text-center">
                <div className="text-lg font-semibold text-success">
                  {data?.workflows.succeeded ?? 0}
                </div>
                <div className="text-xs text-text-muted">Succeeded</div>
              </div>
              <div className="bg-background rounded-lg border border-border p-3 text-center">
                <div className="text-lg font-semibold text-danger">
                  {data?.workflows.failed ?? 0}
                </div>
                <div className="text-xs text-text-muted">Failed</div>
              </div>
            </div>
          </div>

          {/* Lead source breakdown */}
          <div className="bg-surface rounded-xl border border-border p-5">
            <h3 className="text-sm font-medium text-text-secondary mb-3">Lead Sources</h3>
            {isLoading ? (
              <ChartSkeleton />
            ) : (data?.pipeline.bySource.length ?? 0) > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={data?.pipeline.bySource.slice(0, 8).map((s) => ({
                      name: s.source.replace(/_/g, ' '),
                      value: s.count,
                    }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={70}
                    dataKey="value"
                    paddingAngle={2}
                    label={({ name, value }) => `${name} (${value})`}
                  >
                    {data?.pipeline.bySource.slice(0, 8).map((s, i) => (
                      <Cell
                        key={s.source}
                        fill={
                          Object.values(CHANNEL_COLORS).concat([
                            PRIMARY_COLOR,
                            PURPLE_COLOR,
                            WARNING_COLOR,
                            INFO_COLOR,
                          ])[i % 8]
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-text-muted text-sm">
                No source data
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
